import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genBrandField, genSchema } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { CourseStructureFormBrandType } from './courseStructureTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.courseStructure.style, {
  chapterIcons: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.courseStructure.schema.chapterIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false /* resettable: true */ },
    }),
  },
  sectionIcons: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.courseStructure.schema.landingIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false /* resettable: true */ },
    }),
  },
  testIcons: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.courseStructure.schema.testIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false /* resettable: true */ },
    }),
  },
  item_hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.courseStructure.schema.hoverBg'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  item: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.courseStructure.schema.disabledBg'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  progress: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressBackground'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressCompleted'),
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  offsetFromIcon: {
    marginRight: genBrandField({
      as: FieldNumber,
      label: t('input.label.iconIndents'),
      props: { width: 64 },
    }),
  },
  offsetFromSection: {
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('elements.courseStructure.schema.sectionIndents'),
      props: { width: 64 },
    }),
  },
  offsetFromProgress: {
    marginRight: genBrandField({
      as: FieldNumber,
      label: t('elements.courseStructure.schema.progressIndents'),
      props: { width: 64 },
    }),
  },
  offsetFromCollapse: {
    marginRight: genBrandField({
      as: FieldNumber,
      label: t('elements.courseStructure.schema.collapseIndents'),
      props: { width: 64 },
    }),
  },
  collapseIcon: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('course.sidebar.tags.collapse.collapseIconClosed'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false /* resettable: true */ },
    }),
    backgroundOpen: genBrandField({
      as: FieldFileId,
      label: t('course.sidebar.tags.collapse.collapseIconOpen'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
})

const CourseStructureFormStyle: CourseStructureFormBrandType = ({
  device,
  subConfigs,
  edited,
  setForm,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.courseStructure.tags.icon')}</Typography>
        <FormField config={schema.chapterIcons.backgroundImage} />
        <FormField config={schema.sectionIcons.backgroundImage} />
        <FormField config={schema.testIcons.backgroundImage} />

        <Typography weight='medium'>{t('elements.courseStructure.tags.section')}</Typography>
        <FormField config={schema.item_hover.backgroundColor} />
        <FormField config={schema.item.color} />

        <Typography weight='medium'>{t('elements.courseStructure.tags.progress')}</Typography>
        <FormField config={schema.progress.backgroundColor} />
        <FormField config={schema.progress.color} />
        <FormField config={schema.progress.borderStyle} />
        <FormField config={schema.progress.borderColor} />
        <FormField config={schema.progress.borderWidth} />

        <Typography weight='medium'>{t('elements.courseStructure.tags.margins')}</Typography>
        <FormField config={schema.offsetFromIcon.marginRight} />
        <FormField config={schema.offsetFromSection.marginTop} />
        <FormField config={schema.offsetFromProgress.marginRight} />
        <FormField config={schema.offsetFromCollapse.marginRight} />

        <Typography weight='medium'>{t('course.sidebar.tags.collapse')}</Typography>
        <FormField config={schema.collapseIcon.backgroundImage} />
        <FormField config={schema.collapseIcon.backgroundOpen} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(CourseStructureFormStyle) as typeof CourseStructureFormStyle
